import type { HTMLAttributes, ReactNode } from "react";

import { Button, useModal } from "@alpacahq/ui/old";

interface DisclosureProps extends HTMLAttributes<HTMLDivElement> {
  hideLabel?: boolean;
  cryptoBroker?: string;
  children: ReactNode;
}

export const Disclosure = ({
  cryptoBroker,
  children,
  ...props
}: DisclosureProps) => {
  // the current year
  const year = new Date().getFullYear();

  const { component: Modal, toggle } = useModal({
    size: "sm",
    title: "Disclosures",
    controls: (
      <Button variant="alternate" onClick={() => toggle()}>
        Close
      </Button>
    ),
    content: (
      <div>
        <p>
          Brokerage services are provided by Alpaca Securities, member{" "}
          <a href="https://www.finra.org/" target="_blank" rel="noopener">
            FINRA
          </a>
          /
          <a href="https://www.sipc.org/" target="_blank" rel="noopener">
            SIPC
          </a>
          , a wholly-owned subsidiary of AlpacaDB, Inc.
        </p>
        <br />
        <p>Technology and services are offered by AlpacaDB, Inc.</p>
        <br />
        <p>
          Brokerage services are provided to customers who can write automated
          investment code and self-direct their own investments. Alpaca
          brokerage services are only provided to customers who agree to
          electronically sign agreements and agree to receive messages,
          confirmations, and statements electronically.{" "}
          <a
            href="https://alpaca.markets/docs/about-us/#who-is-alpaca-for"
            rel="noopener noreferrer"
            target="_blank"
          >
            Is Alpaca right for me?
          </a>
        </p>
        <br />
        <p>
          This is not an offer, solicitation of an offer, or advice to buy or
          sell securities or cryptocurrencies, or open a brokerage account or
          cryptocurrency account in any jurisdiction where Alpaca Securities,
          Alpaca Crypto respectively, are not registered or licensed, as
          applicable.
        </p>
        <br />
        <p>
          The Paper Trading API is offered by AlpacaDB, Inc. and does not
          require real money or permit a user to transact in real securities in
          the market. Providing use of the Paper Trading API is not an offer or
          solicitation to buy or sell securities, securities derivative or
          futures products of any kind, or any type of trading or investment
          advice, recommendation or strategy, given or in any manner endorsed by
          AlpacaDB, Inc. or any AlpacaDB, Inc. affiliate and the information
          made available through the Paper Trading API is not an offer or
          solicitation of any kind in any jurisdiction where AlpacaDB, Inc. or
          any AlpacaDB, Inc. affiliate (collectively, &#34;Alpaca&#34;) is not
          authorized to do business.
        </p>
        <br />
        <p>
          You should know that the use or granting of any third party access to
          your account information or place transactions in your account at your
          direction is solely at your risk. Alpaca does not warrant against loss
          of use or any direct, indirect or consequential damages or losses to
          you caused by your assent, expressed or implied, to a third party
          accessing your account or information, including access provided
          through any other third party apps, systems, or sites.{" "}
        </p>
        <br />
        <p>
          Market prices, data and other information available through Alpaca are
          not warranted as to completeness or accuracy and are subject to change
          without notice.{" "}
          <em>
            System response and account access times may vary due to a variety
            of factors, including trading volumes, market conditions, system
            performance, and other factors.
          </em>{" "}
          A more complete description of the impact these factors may have can
          be found in our{" "}
          <a
            href="https://alpaca.markets/learn/risks-of-automated-trading-systems/"
            target="_blank"
            rel="noopener"
          >
            risks of automated trading systems section
          </a>
          .{" "}
        </p>
        <br />
        <p>
          All investments involve risk and the past performance of a security,
          or financial product does not guarantee future results or returns.
          Keep in mind that while diversification may help spread risk it does
          not assure a profit, or protect against loss, in a down market. There
          is always the potential of losing money when you invest in securities,
          or other financial products. Investors should consider their
          investment objectives and risks carefully before investing.
        </p>
        <br />
        <p>
          There are risks unique to automated trading algorithms that you should
          know about and plan for. You should setup a method or system of
          continuous monitoring or alerting to let you know if there is a
          mechanical failure, such as connectivity issues, power loss, a
          computer crash, or system quirk. You should also monitor for instances
          where your automated trading system experiences anomalies that could
          result in errant, missing, or duplicated orders. A more complete
          description of these and other risks can be found in our{" "}
          <a
            href="https://alpaca.markets/learn/risks-of-automated-trading-systems/"
            target="_blank"
            rel="noopener"
          >
            FAQ section.
          </a>
        </p>
        <br />
        <p>
          Conditional orders may have increased risk as a result of their
          reliance on trigger processing, market data, and other internal and
          external systems. Such orders are not sent to the market until
          specified conditions are met. During that time, issues such as system
          outages with downstream technologies or third parties may occur.
          Conditional orders triggering near the market close may fail to
          execute that day. Furthermore, our executing partner may impose
          controls on conditional orders to limit erroneous trades triggering
          downstream orders. Alpaca Securities may not always be made aware of
          such changes to external controls immediately, which may lead to some
          conditional orders not being executed. As such, it is important to
          monitor conditional orders for reasonability. Conditional orders are
          “Not Held” orders whose execution instructions are on a best efforts
          basis upon being triggered. Furthermore, conditional orders may be
          subject to the increased risks of stop orders and market orders
          outlined above. Given the increased potential risk of using
          conditional orders, the client agrees that Alpaca Securities cannot be
          held responsible for losses, damages, or missed opportunity costs
          associated with market data problems, systems issues, and user error,
          among other factors. By using conditional orders the client
          understands and accepts the risks outlined above. Alpaca Securities
          encourages leveraging the use of Paper accounts to become more
          comfortable with the intricacies associated with these orders.{" "}
        </p>
        <br />
        <p>
          ETFs can entail risks similar to direct stock ownership, including
          market, sector, or industry risks. Some ETFs may involve international
          risk, currency risk, commodity risk, and interest rate risk. Trading
          prices may not reflect the net asset value of the underlying
          securities.
        </p>
        <br />
        <p>
          All accounts are opened as limited purpose margin accounts. You should
          know that margin trading involves interest charges and risks,
          including the potential to lose more than deposited or the need to
          deposit additional collateral in a falling market. Before using
          margin, customers must determine whether this type of trading strategy
          is right for them given their specific investment objectives,
          experience, risk tolerance, and financial situation. For more
          information please see{" "}
          <a
            href="https://files.alpaca.markets/disclosures/library/MarginDiscStmt.pdf"
            target="_blank"
            rel="noopener"
          >
            Alpaca’s Margin Disclosure Statement
          </a>{" "}
          and{" "}
          <a
            href="https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf"
            target="_blank"
            rel="noopener"
          >
            Margin Agreement.
          </a>{" "}
          These disclosures contain information on Alpaca Securities&#39;s
          lending policies, interest charges, and the risks associated with
          margin accounts.
        </p>
        <br />
        <p>
          Commission-Free trading means that there are no commission charges for
          Alpaca Securities self-directed individual brokerage accounts that
          trade U.S. listed securities through an API.{" "}
          <a
            href="https://files.alpaca.markets/disclosures/library/BrokFeeSched.pdf"
            target="_blank"
            rel="noopener"
          >
            Relevant SEC and FINRA fees may apply.
          </a>
        </p>
        <br />
        <p>
          Cryptocurrency is highly speculative in nature, involves a high degree
          of risks, such as volatile market price swings, market manipulation,
          flash crashes, and cybersecurity risks. Cryptocurrency is not
          regulated or is lightly regulated in most countries. Cryptocurrency
          trading can lead to large, immediate and permanent loss of financial
          value. You should have appropriate knowledge and experience before
          engaging in cryptocurrency trading. For additional information please
          click{" "}
          <a
            href="https://files.alpaca.markets/disclosures/library/CryptoRiskDisclosures.pdf"
            target="_blank"
            rel="noopener"
            aria-label="Cryptocurrency risk disclosures (PDF)"
          >
            here
          </a>
          .
        </p>
        <br />
        {cryptoBroker === "BS" ? <AlpacaXCryptoCopy /> : <DefaultCryptoCopy />}
        <br />
        <p>
          &copy; {year} AlpacaDB, Inc All rights reserved.
          <br />
          &copy; {year} Alpaca Securities LLC All rights reserved.
          <br />
          &copy; {year} Alpaca{cryptoBroker === "BS" && "X"} Crypto LLC All
          rights reserved.
        </p>
      </div>
    ),
  });

  return (
    <>
      <div {...props} onClick={toggle}>
        {children}
      </div>
      {Modal}
    </>
  );
};

const DefaultCryptoCopy = () => {
  return (
    <p>
      Cryptocurrency services are made available by Alpaca Crypto, a FinCEN
      registered money services business (NMLS # 2160858), and a wholly-owned
      subsidiary of AlpacaDB, Inc. Alpaca Crypto is not a member of SIPC or
      FINRA. Cryptocurrencies are not stocks and your cryptocurrency investments
      are not protected by either FDIC or SIPC. Please see the{" "}
      <a
        href="https://alpaca.markets/disclosures"
        target="_blank"
        rel="noopener"
      >
        Disclosure Library
      </a>{" "}
      for more information.
    </p>
  );
};

const AlpacaXCryptoCopy = () => {
  return (
    <p>
      Alpaca Crypto is authorized and regulated by The Securities Commission of
      the Bahamas. Alpaca Crypto is an international business company registered
      in the Commonwealth of The Bahamas, registration number 208648 B. Alpaca
      Crypto is not a member of SIPC or FINRA. Cryptocurrencies are not stocks
      and your cryptocurrency investments are not protected by either FDIC or
      SIPC. Please see the{" "}
      <a
        href="https://alpaca.markets/disclosures"
        target="_blank"
        rel="noopener"
      >
        Disclosure Library
      </a>{" "}
      for more information.
    </p>
  );
};
